import React, { useState, useMemo } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { useTranslation } from "@/lib/i18n";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, FileSearch, Shield, Terminal, Bot } from "lucide-react";
import { motion } from "framer-motion";
const MOCK_LOGS = [
  { id: "1", type: "security", event: "Admin Login", user: "tongstv@gmail.com", severity: "info", timestamp: "2024-03-10 10:24:05" },
  { id: "2", type: "system", event: "DurableObject Backup", user: "system_cron", severity: "info", timestamp: "2024-03-10 09:00:00" },
  { id: "3", type: "security", event: "Failed Login Attempt", user: "ip_192.168.1.1", severity: "critical", timestamp: "2024-03-10 08:45:12" },
  { id: "4", type: "agent", event: "Agent Deployed", user: "admin@cloudface.com", severity: "warning", timestamp: "2024-03-10 07:12:44" },
  { id: "5", type: "system", event: "Policy Updated", user: "admin@cloudface.com", severity: "info", timestamp: "2024-03-09 23:55:01" },
  { id: "6", type: "agent", event: "Usage Limit Reached", user: "agent-1", severity: "warning", timestamp: "2024-03-09 22:30:15" },
];
export default function AuditLogsPage() {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const filteredLogs = useMemo(() => {
    return MOCK_LOGS.filter(log => {
      const matchesSearch = log.event.toLowerCase().includes(search.toLowerCase()) ||
                            log.user.toLowerCase().includes(search.toLowerCase());
      const matchesFilter = filter === "all" || log.type === filter;
      return matchesSearch && matchesFilter;
    });
  }, [search, filter]);
  const severityColors = {
    info: "bg-primary/10 text-primary border-primary/20",
    warning: "bg-amber-500/10 text-amber-500 border-amber-500/20",
    critical: "bg-destructive/10 text-destructive border-destructive/20"
  };
  const typeIcons = {
    security: <Shield className="w-3.5 h-3.5" />,
    system: <Terminal className="w-3.5 h-3.5" />,
    agent: <Bot className="w-3.5 h-3.5" />
  };
  return (
    <AppLayout container>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('audit_logs')}</h1>
          <p className="text-muted-foreground mt-1">
            Comprehensive audit trail of all enterprise activities and security events.
          </p>
        </div>
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-card/50 p-4 rounded-xl border border-white/5 backdrop-blur-sm">
          <div className="relative w-full md:max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={t('search_logs')}
              className="pl-10 bg-background/50"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-full md:w-[200px] bg-background/50">
              <FileSearch className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('filter_all')}</SelectItem>
              <SelectItem value="security">{t('security_event')}</SelectItem>
              <SelectItem value="system">{t('system_event')}</SelectItem>
              <SelectItem value="agent">{t('agent_event')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-xl border border-white/5 bg-card/50 overflow-hidden"
        >
          <Table>
            <TableHeader className="bg-muted/30">
              <TableRow>
                <TableHead>{t('timestamp')}</TableHead>
                <TableHead>{t('event_type')}</TableHead>
                <TableHead>Event</TableHead>
                <TableHead>{t('user_id')}</TableHead>
                <TableHead>{t('severity')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLogs.map((log) => (
                <TableRow key={log.id} className="hover:bg-muted/20">
                  <TableCell className="font-mono text-xs whitespace-nowrap">
                    {log.timestamp}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 capitalize text-xs">
                      {typeIcons[log.type as keyof typeof typeIcons]}
                      {t(`${log.type}_event` as any)}
                    </div>
                  </TableCell>
                  <TableCell className="font-medium text-sm">
                    {log.event}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {log.user}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`capitalize text-[10px] ${severityColors[log.severity as keyof typeof severityColors]}`}>
                      {t(log.severity as any)}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </motion.div>
      </div>
    </AppLayout>
  );
}
